/**
 * Pay: the report, payroll types and rate rules, and the overtime bank.
 */
import { del, get, post, put } from '@/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** The overtime bank, plan 0027 G2: worked out from approved overtime and approved Avspasering. */
export interface ApiTimeBank {
  agreed_on: string | null;
  share: number | null;
  hours_in: number;
  hours_taken: number;
  balance: number;
  /** each ISO week (its Monday) with overtime since the agreement, and what went in at the share */
  weeks: { week: string; overtime: number; hours_in: number }[];
  taken: { id: number; from: string; to: string; hours: number }[];
  /** flexitime (slice 4): each finished week, hours worked up to the overtime line against the expected */
  flex?: {
    agreed_on: string;
    hours: number;
    weeks: { week: string; worked: number; expected: number; flex: number }[];
  } | null;
  /** by hand (slice 3): `hours` is the change to the balance, so a payout is negative */
  entries: { id: number; date: string; hours: number; kind: 'adjustment' | 'payout'; note: string | null }[];
}
/** POST /admin/users/:id/timebank/entries (pay.view): an adjustment with a note, or a payout. */
export function useTimeBankWrites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: { kind: 'adjustment' | 'payout'; hours: number; date?: string; note?: string };
    }) => post<ApiTimeBank>(`/admin/users/${id}/timebank/entries`, body),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['timebank'] }),
        qc.invalidateQueries({ queryKey: ['report'] }),
      ]),
  });
}
/** GET /admin/timebank (pay.view): each person with a bank, for Timer til lønn's checks. */
export interface ApiCompanyBank {
  user_id: number;
  name: string;
  lastname: string | null;
  active: boolean;
  balance: number;
}
export function useCompanyTimeBank(enabled = true) {
  return useQuery({
    queryKey: ['timebank', 'company'],
    queryFn: () => get<ApiCompanyBank[]>('/admin/timebank'),
    enabled,
  });
}
/** GET /admin/users/:id/timebank (people.manage or pay.view), or the caller's own with `mine`. */
export function useTimeBank(id: number | 'mine' | null) {
  return useQuery({
    queryKey: ['timebank', id],
    queryFn: () => get<ApiTimeBank>(id === 'mine' ? '/timebank/mine' : `/admin/users/${id}/timebank`),
    enabled: id != null,
  });
}
export interface ApiPayrollType {
  id: number;
  name: string;
  code: string;
  rateType: 'extra_per_hour' | 'percent' | string;
  value: string;
  ruleCount: number;
  /** #143: not paid on an hour that also carries this type (Helg yielding to Natt); null stacks */
  yieldsTo?: number | null;
  /** #183: the lønnsart pays the whole hour, base and supplement; its hours leave Vanlige timer */
  paysWholeHour?: boolean;
}
export interface ApiRateRule {
  id: number;
  priority: number;
  day: number;
  effective_start: string | null;
  effective_end: string | null;
  start_time: string | null;
  end_time: string | null;
  description: string | null;
  threshold: number | null;
  payroll_type_id: number;
}
export function usePayrollTypes() {
  return useQuery({
    queryKey: ['payroll-types'],
    queryFn: () => get<ApiPayrollType[]>('/admin/payroll/types'),
    staleTime: 5 * 60_000,
  });
}
export function useRateRules() {
  return useQuery({
    queryKey: ['rate-rules'],
    queryFn: () => get<ApiRateRule[]>('/admin/payroll/rules'),
    staleTime: 5 * 60_000,
  });
}
/** Engine A: approved shifts only, punch times, per pay type (payroll.md §3, §5). */
export interface ReportLine {
  /** A report over several stores (plan 0031): the hours of this line per store */
  stores?: { tenant_id: number; name: string; hours: number }[];
  /** Decided where the line is built (admin/shiftController.js buildPayrollReportObject); absent from older servers. */
  kind?: 'base' | 'supplement' | 'overtime' | 'absence';
  hours: number;
  /** Rate rules only: the payroll type's calculation_type. The base line and absence lines have none. */
  type?: string;
  rate_value: string;
  /** null on a rate-rule line for a person with no hourly rate: the payroll system prices it (CV4, until D35) */
  rate: number | null;
  salary_total: number;
  salary_type_code?: string;
  /** 'nav' on an absence line holding the days after the employer's (the "<type> (NAV)" line, 0 kr) */
  paid_by?: 'nav';
  /** Days on this paid line the company claims back from NAV: sykt barn past the tenth (CV1) */
  refund_days?: number;
  /** 'dager': a sick or sykt barn line counted in days, `hours` holding the days and `rate` the dagsats (D18) */
  unit?: 'dager';
  /** the dagsats rests on less than three months of work in Timefly, or none (rate then null) */
  basis_short?: boolean;
  /** why an absence line has what it has (sick days, ammefri, avspasering): see LINE_NOTES in pages/lonn/run.ts */
  notes?: string[];
  /** D19: days (YYYY-MM-DD) this paid whole-day absence shares with approved work, paid as both */
  worked_dates?: string[];
  /** hours on this line per project id ('' is no project), which the Tripletex file writes as rows per Prosjektnummer (#115) */
  projects?: Record<string, number>;
}
export interface ReportRow {
  id: string;
  name: string;
  employee_id?: string | null;
  totals: Record<string, ReportLine>;
  /** approved hours on the base line with no project (#115), for Kontroller where a project is required */
  hours_without_project?: number;
}
/** `scope` is '' for the active store, or '&stores=…' for several (plan 0031, lonn/Scope.tsx) */
export function useReport(start: string, end: string, scope = '') {
  return useQuery({
    queryKey: ['report', start, end, scope],
    queryFn: () =>
      // revision: the lines' checksum, passed back with a file so changed hours are never exported unseen (#265)
      get<{ payroll: ReportRow[]; csv: string; revision?: string }>(
        `/admin/report?start_date=${start}&end_date=${end}${scope}`,
      ),
    staleTime: 30_000,
  });
}
/* ── rate rules and payroll types, write side (web-configuration.md §6). The rule routes carry the id as ?id=, not in the path. ─── */
export interface RateRuleWrite {
  payroll_type_id: number;
  name?: string;
  priority?: number;
  day: number;
  description?: string | null;
  effective_start?: string | null;
  effective_end?: string | null;
  time_from?: string | null;
  time_to?: string | null;
  threshold?: number | null;
}
export interface PayrollTypeWrite {
  name: string;
  code: string;
  calculation_type: 'extra_per_hour' | 'percent';
  rate_value: number;
  yields_to_payroll_type_id?: number | null;
  pays_whole_hour?: boolean;
}
export function usePayrollWrites() {
  const qc = useQueryClient();
  const done = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['payroll-types'] }),
      qc.invalidateQueries({ queryKey: ['rate-rules'] }),
    ]);
  return {
    createRule: useMutation({
      mutationFn: (b: RateRuleWrite) => post<unknown>('/admin/payroll/rules', b),
      onSettled: done,
    }),
    updateRule: useMutation({
      mutationFn: ({ id, body }: { id: number; body: Partial<RateRuleWrite> }) =>
        put<unknown>(`/admin/payroll/rules?id=${id}`, body),
      onSettled: done,
    }),
    removeRule: useMutation({
      mutationFn: (id: number) => del<unknown>(`/admin/payroll/rules?id=${id}`),
      onSettled: done,
    }),
    createType: useMutation({
      mutationFn: (b: PayrollTypeWrite) => post<unknown>('/admin/payroll/types', b),
      onSettled: done,
    }),
    updateType: useMutation({
      mutationFn: ({ id, body }: { id: number; body: Partial<PayrollTypeWrite> }) =>
        put<unknown>(`/admin/payroll/types/${id}`, body),
      onSettled: done,
    }),
  };
}
